"use client";

import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import {
  cleanupOrphanedModalSideEffects,
  MODAL_EXIT_ANIMATION_MS,
} from "@/lib/utils/modalSideEffectCleanup";
import { cn } from "@/lib/utils/cn";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

const sizeClassName = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  /** Wide split layouts (e.g. preview + form). */
  "3xl": "max-w-6xl",
  /** Extra-wide split forms (e.g. add insurance). */
  "4xl": "max-w-7xl",
  /** Editor flows that need the entire viewport. */
  full: "max-w-none",
} as const;

function SplitBody({
  bodyClassName,
  previewColumnClassName,
  previewLabel,
  bodyFormId,
  bodyFormOnSubmit,
  preview,
  previewPosition,
  children,
}: {
  bodyClassName?: string;
  previewColumnClassName?: string;
  previewLabel?: string | null;
  bodyFormId?: string;
  bodyFormOnSubmit?: React.FormEventHandler<HTMLFormElement>;
  preview: React.ReactNode;
  previewPosition: "left" | "right";
  children: React.ReactNode;
}) {
  const row = (
    <div
      className={cn("flex min-h-0 flex-1 flex-col lg:flex-row", bodyClassName)}
    >
      <div
        className={cn(
          "border-border flex max-h-[min(40vh,320px)] min-h-0 shrink-0 flex-col overflow-hidden border-b",
          "lg:max-h-none lg:w-[min(100%,360px)] lg:max-w-[360px] lg:self-stretch lg:border-r lg:border-b-0",
          previewPosition === "right" && "lg:order-2 lg:border-r-0 lg:border-l",
          previewColumnClassName,
        )}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="px-6 py-5">
            {previewLabel != null && previewLabel !== "" ? (
              <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
                {previewLabel}
              </p>
            ) : null}
            {preview}
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );

  if (bodyFormId) {
    return (
      <form
        id={bodyFormId}
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={bodyFormOnSubmit}
      >
        {row}
      </form>
    );
  }

  return row;
}

export type AppModalSize = keyof typeof sizeClassName;

export interface AppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modal?: boolean;
  title: string;
  /** Content displayed immediately after the modal title, such as a help tooltip. */
  titleTrailing?: React.ReactNode;
  /** Override the default initial focus behavior when the modal opens. */
  onOpenAutoFocus?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >["onOpenAutoFocus"];
  onCloseAutoFocus?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >["onCloseAutoFocus"];
  description?: string;
  /**
   * Optional left column (e.g. contact preview). Body becomes a two-column split
   * on large screens; reuse for future “edit as modal” flows.
   */
  preview?: React.ReactNode;
  /** Override or hide the default “Preview” label (`null` hides). */
  previewLabel?: string | null;
  /** Desktop position for the preview column. Mobile remains stacked. */
  previewPosition?: "left" | "right";
  /** Extra classes for the left column wrapper. */
  previewColumnClassName?: string;
  /**
   * With `preview`, wraps both columns in `<form>` so fields in either column
   * belong to one submit. Pair footer buttons with `form={bodyFormId}`.
   */
  bodyFormId?: string;
  bodyFormOnSubmit?: React.FormEventHandler<HTMLFormElement>;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: AppModalSize;
  /**
   * Backdrop blur + tint (default on, matches standard dialogs). Set `false`
   * for a dim-only overlay without blur.
   */
  overlayBlur?: boolean;
  /** Merged onto the overlay (e.g. stronger blur / z-index when stacking over another dialog). */
  overlayClassName?: string;
  /** When true, blocks interaction and shows one modal-wide loading state. */
  loading?: boolean;
  /**
   * When true, overlay / escape / close attempt shows the same unsaved-changes
   * confirmation as `Slideover` instead of closing immediately.
   */
  isDirty?: boolean;
  className?: string;
  bodyClassName?: string;
  /** Classes for the inner body content wrapper. */
  bodyContentClassName?: string;
  /**
   * When true (e.g. embedded split forms), the body area is flex-constrained and does not
   * scroll as a whole — children must define their own `overflow-y-auto` regions. Fixes
   * clipped content when the outer body uses `overflow-hidden`.
   */
  bodyContained?: boolean;
  hideCloseButton?: boolean;
  hideHeader?: boolean;
}

const isModalSafeOutsideInteraction = (
  event: CustomEvent<{ originalEvent: Event }>,
) => {
  const target = event.detail.originalEvent.target;
  return (
    target instanceof Element &&
    target.closest('[data-preserve-open-modal="true"]') != null
  );
};

/**
 * Centered modal shell for app-wide use: title, optional description, scrollable
 * body, optional footer. Overlay uses dim + backdrop blur by default.
 *
 * Layering (see shadcn primitives): overlay `z-[100]`, content `z-[101]`.
 * For a second dialog on top (e.g. contact picker), pass `overlayClassName` /
 * `className` with higher z (e.g. `z-[108]` / `z-[109]`) so the overlay covers
 * the first dialog’s content; keep below `z-[110]` popovers.
 * Sheets use102/103; portaled popovers/menus/selects use `z-[110]` so filters
 * and dropdowns stay usable above this modal and above slideovers.
 *
 * Pass `preview` for a reusable split layout (narrow preview column + scrollable
 * main); pair with `size="3xl"` for edit flows that mirror a slideover’s width.
 *
 * Pass `isDirty` with your form’s dirty flag so outside click / escape / X match
 * slideover behavior (confirm before discard).
 */
export function AppModal({
  open,
  onOpenChange,
  modal = true,
  title,
  titleTrailing,
  onOpenAutoFocus,
  onCloseAutoFocus,
  description,
  preview,
  previewLabel = "Preview",
  previewPosition = "left",
  previewColumnClassName,
  bodyFormId,
  bodyFormOnSubmit,
  children,
  footer,
  size = "lg",
  overlayBlur = true,
  overlayClassName,
  loading = false,
  isDirty = false,
  className,
  bodyClassName,
  bodyContentClassName,
  bodyContained = false,
  hideCloseButton = false,
  hideHeader = false,
}: AppModalProps) {
  const [confirmClose, setConfirmClose] = React.useState(false);

  const hasOpenModalPopover = React.useCallback(() => {
    if (typeof document === "undefined") {
      return false;
    }

    return document.querySelector('[data-modal-popover="true"]') != null;
  }, []);

  React.useEffect(() => {
    if (!open) {
      setConfirmClose(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cleanupOrphanedModalSideEffects();
    }, MODAL_EXIT_ANIMATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open]);

  React.useEffect(() => {
    return () => {
      window.setTimeout(
        cleanupOrphanedModalSideEffects,
        MODAL_EXIT_ANIMATION_MS,
      );
    };
  }, []);

  return (
    <>
      <DialogPrimitive.Root
        modal={modal}
        open={open}
        onOpenChange={(nextOpen) => {
          if (loading) return;
          if (!nextOpen && hasOpenModalPopover()) {
            return;
          }
          if (!nextOpen && isDirty) {
            setConfirmClose(true);
            return;
          }
          onOpenChange(nextOpen);
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100]",
              overlayBlur
                ? "bg-black/45 backdrop-blur-[2px]"
                : "bg-black/55 backdrop-blur-none",
              overlayClassName,
            )}
          />
          <DialogPrimitive.Content
            onOpenAutoFocus={onOpenAutoFocus}
            onCloseAutoFocus={onCloseAutoFocus}
            onEscapeKeyDown={(event) => {
              if (loading) {
                event.preventDefault();
                return;
              }
              if (hasOpenModalPopover()) {
                event.preventDefault();
                return;
              }
              if (isDirty) {
                event.preventDefault();
                setConfirmClose(true);
              }
            }}
            onPointerDownOutside={(event) => {
              if (isModalSafeOutsideInteraction(event)) {
                event.preventDefault();
                return;
              }
              if (loading) {
                event.preventDefault();
                return;
              }
              if (hasOpenModalPopover()) {
                event.preventDefault();
                return;
              }
              if (isDirty) {
                event.preventDefault();
                setConfirmClose(true);
              }
            }}
            onFocusOutside={(event) => {
              if (
                isModalSafeOutsideInteraction(event) ||
                hasOpenModalPopover()
              ) {
                event.preventDefault();
              }
            }}
            className={cn(
              "bg-card border-border/80 fixed top-[50%] left-[50%] z-[101] flex origin-center translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden p-0 shadow-[0_8px_28px_rgba(0,0,0,0.28)] duration-200",
              size === "full"
                ? "h-dvh max-h-dvh w-screen rounded-none border-0 sm:w-screen"
                : "max-h-[min(90dvh,880px)] w-[calc(100vw-2rem)] rounded-[2rem] border sm:w-[calc(100vw-3rem)]",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              sizeClassName[size],
              className,
            )}
          >
            {hideHeader ? (
              <>
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <DialogDescription className="sr-only">
                  {description || "Modal content"}
                </DialogDescription>
              </>
            ) : (
              <DialogHeader className="border-border relative z-[1] min-w-0 shrink-0 border-b px-6 py-5 pr-14 text-left">
                <DialogTitle
                  data-testid="slideover-title"
                  className="flex min-w-0 items-center gap-1 text-lg leading-6 font-medium tracking-normal break-words"
                  title={title}
                >
                  <span className="line-clamp-4 min-w-0">{title}</span>
                  {titleTrailing}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {description || "Modal content"}
                </DialogDescription>
                {!hideCloseButton ? (
                  <DialogPrimitive.Close
                    type="button"
                    disabled={loading}
                    className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-1/2 right-5 z-[3] grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
                ) : null}
              </DialogHeader>
            )}
            {preview != null ? (
              <SplitBody
                bodyClassName={bodyClassName}
                previewColumnClassName={previewColumnClassName}
                previewLabel={previewLabel}
                previewPosition={previewPosition}
                bodyFormId={bodyFormId}
                bodyFormOnSubmit={bodyFormOnSubmit}
                preview={preview}
              >
                {children}
              </SplitBody>
            ) : bodyFormId ? (
              <form
                id={bodyFormId}
                className={cn(
                  "flex min-h-0 flex-1 flex-col",
                  bodyContained ? "overflow-hidden" : "overflow-y-auto",
                  bodyClassName,
                )}
                onSubmit={bodyFormOnSubmit}
              >
                <div
                  className={cn(
                    "flex flex-col",
                    bodyContained
                      ? "h-full min-h-0 flex-1 overflow-hidden"
                      : "min-h-full px-6 py-5",
                    bodyContentClassName,
                  )}
                >
                  {children}
                </div>
              </form>
            ) : (
              <div
                className={cn(
                  "min-h-0 flex-1",
                  bodyContained
                    ? "flex flex-col overflow-hidden"
                    : "overflow-y-auto",
                  bodyClassName,
                )}
              >
                <div
                  className={cn(
                    "flex flex-col",
                    bodyContained
                      ? "h-full min-h-0 flex-1 overflow-hidden"
                      : "min-h-full px-6 py-5",
                  )}
                >
                  {children}
                </div>
              </div>
            )}
            {footer ? (
              <div className="border-border relative z-[1] shrink-0 border-t px-6 py-4">
                {footer}
              </div>
            ) : null}
            {loading ? (
              <div className="bg-card/70 absolute inset-0 z-[50] flex items-center justify-center backdrop-blur-[1px]">
                <LoadingSpinner className="h-8 w-8" />
              </div>
            ) : null}
            {!hideCloseButton && hideHeader ? (
              <DialogPrimitive.Close
                type="button"
                disabled={loading}
                className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-5 z-[3] grid h-8 w-8 place-items-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            ) : null}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        buttonText="Confirm"
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        afterActionFn={() => {
          onOpenChange(false);
          setConfirmClose(false);
        }}
      />
    </>
  );
}
